import { useEffect, useState } from 'react';
import { ChevronLeft, ChevronRight, ExternalLink, ImageIcon, Pause, Play } from 'lucide-react';
import BrandLoading from '../ui/BrandLoading.jsx';
import { assetUrl } from '../../utils/assetUrl.js';
import { ASSOCIATE_PORTAL_URL } from '../../constants/associatePortal.js';
import publicCta from '../../content/publicCta.json';

const AUTOPLAY_MS = 7000;

export default function HeroCarousel({ slides, loading }) {
  const [index, setIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(true);

  useEffect(() => {
    setIndex(0);
  }, [slides]);

  useEffect(() => {
    if (!isPlaying || !slides?.length || slides.length < 2) return undefined;
    const id = window.setInterval(() => {
      setIndex((i) => (i + 1) % slides.length);
    }, AUTOPLAY_MS);
    return () => window.clearInterval(id);
  }, [slides, isPlaying]);

  if (loading) {
    return <BrandLoading variant="section" label="Cargando contenido…" />;
  }

  if (!slides?.length) {
    return (
      <section className="relative flex min-h-[68vh] items-center overflow-hidden bg-brand-navy">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top,_rgba(103,178,53,0.35),_transparent_55%)]" />
        <div className="relative z-10 mx-auto max-w-5xl px-6 py-24 text-center">
          <p className="text-xs font-semibold uppercase tracking-[0.35em] text-accent-lime">Coonaltragas</p>
          <h1 className="mt-4 font-display text-4xl font-bold text-balance text-white md:text-5xl lg:text-6xl">
            Actualiza tus datos como asociado
          </h1>
          <p className="mx-auto mt-6 max-w-2xl text-lg text-white/85">
            Recordatorios y comunicados de la cooperativa para que mantengas tu información al día. Cuando haya slides
            activos, el carrusel mostrará lo que publique administración.
          </p>
        </div>
      </section>
    );
  }

  const slide = slides[index];
  const imgSrc = slide.imagen ? assetUrl(slide.imagen) : '';
  const slideHref = String(slide.boton_url || '').trim() || ASSOCIATE_PORTAL_URL;
  const slideIsExternal = /^https?:\/\//i.test(slideHref);
  const buttonLabel = String(slide.boton_texto || '').trim() || publicCta.carouselFallbackButtonText;

  function openSlideLink() {
    if (!slideHref || slideHref === '#') return;
    if (slideIsExternal) {
      window.open(slideHref, '_blank', 'noopener,noreferrer');
    } else {
      window.location.assign(slideHref);
    }
  }

  return (
    <section className="relative min-h-[68vh] overflow-hidden bg-brand-navy text-white">
      <div className="pointer-events-none absolute inset-0 bg-gradient-to-b from-brand-navy via-[#0a1435] to-brand-black" />

      <div className="relative z-10 mx-auto flex min-h-[68vh] max-w-7xl flex-col justify-center px-6 py-14 lg:py-20">
        <div
          key={slide.id ?? index}
          role="button"
          tabIndex={0}
          onClick={openSlideLink}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              openSlideLink();
            }
          }}
          className="grid w-full cursor-pointer items-center gap-10 rounded-2xl text-left outline-none ring-offset-2 ring-offset-brand-navy transition duration-300 hover:opacity-[0.97] focus-visible:ring-2 focus-visible:ring-[#1acb12] lg:grid-cols-12 lg:gap-12 xl:gap-16"
          aria-label={`${slide.titulo || 'Slide'}: abrir enlace`}
        >
          <div className="order-2 max-w-xl animate-fade-up motion-reduce:animate-none motion-reduce:opacity-100 motion-reduce:transform-none lg:order-1 lg:col-span-5 lg:max-w-none xl:col-span-4">
            <p className="text-xs font-semibold uppercase tracking-[0.35em] text-accent-lime">
              {slide.subtitulo || 'Recordatorio asociados'}
            </p>
            <h1 className="mt-4 font-display text-4xl font-bold leading-tight text-balance text-white md:text-5xl lg:text-[2.75rem] lg:leading-[1.08] xl:text-6xl">
              {slide.titulo}
            </h1>
            {slide.descripcion && (
              <p className="mt-6 max-w-xl text-lg leading-relaxed text-white/88">{slide.descripcion}</p>
            )}
            <div className="mt-10 flex flex-wrap gap-4">
              <span className="inline-flex items-center justify-center gap-2 rounded-lg border border-transparent bg-brand-green px-5 py-2.5 text-sm font-semibold text-white shadow-sm">
                {buttonLabel}
                {slideIsExternal ? (
                  <ExternalLink className="h-4 w-4 shrink-0" aria-hidden />
                ) : (
                  <ChevronRight className="h-4 w-4 shrink-0" aria-hidden />
                )}
              </span>
            </div>
          </div>

          <div className="order-1 w-full min-w-0 lg:order-2 lg:col-span-7 xl:col-span-8">
            {imgSrc ? (
              <figure className="mx-auto flex w-full items-center justify-center overflow-hidden rounded-2xl border border-white/20 bg-slate-950/40 p-2 shadow-[0_24px_48px_-12px_rgba(0,0,0,0.45)] sm:p-3 lg:mx-0 lg:ml-auto">
                <img
                  src={imgSrc}
                  alt={slide.titulo ? String(slide.titulo) : ''}
                  className="h-auto max-h-32 w-auto max-w-full object-contain sm:max-h-36 md:max-h-40 lg:max-h-[min(52vh,420px)] xl:max-h-[min(64vh,520px)]"
                  loading={index === 0 ? 'eager' : 'lazy'}
                  decoding="async"
                />
              </figure>
            ) : (
              <div className="mx-auto flex min-h-[200px] w-full flex-col items-center justify-center gap-3 rounded-2xl border border-dashed border-white/20 bg-white/[0.04] text-white/50 lg:mx-0 lg:ml-auto lg:min-h-[240px]">
                <ImageIcon className="h-12 w-12 opacity-60" aria-hidden />
                <span className="text-sm font-medium">Sin imagen en este mensaje</span>
              </div>
            )}
          </div>
        </div>

        <div className="mt-12 flex flex-wrap items-center justify-center gap-4 lg:mt-14">
          <button
            type="button"
            className="rounded-full border border-white/25 bg-white/10 p-3 text-white transition-colors hover:bg-white/20"
            onClick={() => setIndex((i) => (i - 1 + slides.length) % slides.length)}
            aria-label="Anterior"
          >
            <ChevronLeft className="h-6 w-6" />
          </button>
          <div className="flex gap-2">
            {slides.map((_, i) => (
              <button
                key={i}
                type="button"
                aria-label={`Ir al slide ${i + 1}`}
                onClick={() => setIndex(i)}
                className={`h-2 rounded-full transition-all ${
                  i === index ? 'w-8 bg-[#1acb12]' : 'w-2 bg-white/30 hover:bg-white/50'
                }`}
              />
            ))}
          </div>
          <button
            type="button"
            className="rounded-full border border-white/25 bg-white/10 p-3 text-white transition-colors hover:bg-white/20"
            onClick={() => setIndex((i) => (i + 1) % slides.length)}
            aria-label="Siguiente"
          >
            <ChevronRight className="h-6 w-6" />
          </button>
          {slides.length > 1 && (
            <button
              type="button"
              className="rounded-full border border-white/25 bg-white/10 p-3 text-white transition-colors hover:bg-white/20"
              onClick={() => setIsPlaying((playing) => !playing)}
              aria-label={isPlaying ? 'Pausar carrusel' : 'Reanudar carrusel'}
              aria-pressed={isPlaying}
            >
              {isPlaying ? <Pause className="h-6 w-6" /> : <Play className="h-6 w-6" />}
            </button>
          )}
        </div>
      </div>
    </section>
  );
}
